import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useMatch } from 'react-router-dom';
import { connect } from 'react-redux';
import moment from 'moment';
import queryString from 'query-string';
import axios from 'config/axios';
import Auth from 'Api/Auth';
import Users from 'Api/Users';
import { parseJwt } from 'utils';
import { setCurrentTeacher } from 'store/actions/authActions';
import store from 'store';

const LegacyPrivateRoute = ({ routeType, auth, embed, accountExpired, redirect, children, render, ...props }) => {
    const { isAuthenticated } = auth;
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const match = useMatch(location.pathname);
    const isExpired = !moment().isBefore(moment(auth.expiryDate));
    const { redirectUri, _rt } = queryString.parse(new URL(window.location.href).search);
    
    // Create router props for compatibility
    const routerProps = {
        location,
        history: {
            push: (path, state) => navigate(path, { state, replace: false }),
            replace: (path, state) => navigate(path, { state, replace: true }),
            goBack: () => navigate(-1),
            goForward: () => navigate(1),
            go: (n) => navigate(n),
            location,
            action: 'PUSH',
        },
        match: match ? {
            params,
            isExact: match.pathname === location.pathname,
            path: match.pathnameBase || location.pathname,
            url: match.pathname || location.pathname,
        } : {
            params,
            isExact: true,
            path: location.pathname,
            url: location.pathname,
        },
    };

    useEffect(() => {
        if (_rt && !embed) {
            axios.defaults.headers.common['x-refresh-token'] = _rt;
            localStorage.setItem('lsgoRT', _rt);
            Auth.userV2({
                redirect: true,
                history: { push: navigate, location },
                redirectUri,
            }).then(async ({ token, intercomToken }) => {
                if (token) {
                    const decoded = await parseJwt(token.replace('Bearer ', ''));
                    if (decoded.id) {
                        try {
                            const profile = await Users.getUserProfile(decoded.id);
                            if (!profile.data.name) profile.data.name = profile.data.firstName;
                            store.dispatch(setCurrentTeacher({ ...profile.data, alias: decoded.alias }));
                        } catch (error) {
                            console.log(error, 'error');
                        }
                    }
                    setTimeout(() => navigate(redirectUri || '/'), 500);
                }
            });
        }
    }, [_rt, embed, navigate, location, redirectUri]);

    // For embed routes (public routes like /forms, /about), always render regardless of auth
    if (embed) {
        return render ? render({ ...props, ...routerProps }) : children;
    }

    if (isAuthenticated) {
        if (redirectUri || redirect) {
            return <Navigate to={redirectUri || redirect} replace />;
        } else if (routeType === 'guest') {
            return <Navigate to='/teacher' replace />;
        } else if (isAuthenticated && isExpired && !location.pathname.includes('/teacher/profile')) {
            return <Navigate to='/teacher/profile' replace />;
        }
    } else {
        if (['sentral', 'schoolBytes'].includes(routeType))
            return (
                <Navigate
                    to={`/?redirectUri=${encodeURIComponent(
                        location.pathname + location.search
                    )}&auth_method=${routeType === 'schoolBytes' ? 'schoolBytes_sso' : 'sentral_sso'}`}
                    replace
                />
            );

        if (routeType === 'private' || !routeType)
            return (
                <Navigate
                    to={`/?redirectUri=${encodeURIComponent(location.pathname + location.search)}`}
                    replace
                />
            );
    }

    return render ? render({ ...props, ...routerProps }) : children;
};

export default connect((state) => {
    return { auth: state.auth };
})(LegacyPrivateRoute);
